import Alertize from 'app/shared/Alfacom/Alert/Alertize'
import {VALIDATION_ERROR} from 'app/shared/Common/Constants'
import {GetFormikValidClass} from 'app/shared/Common/Util'
import FormikErrors from 'app/shared/FormikErrors/FormikErrors'
import {useFormik} from 'formik'
import React, {useState} from 'react'
import {Button, Col, Form, Image, Row} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import * as Yup from 'yup'
import {toAbsoluteUrl} from '_metronic/helpers'
import {requestPasswordReset, resendEmailConfirmation} from '../core/_requests'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string().trim()
    .email(VALIDATION_ERROR.STRING.EMAIL())
    .max(256, VALIDATION_ERROR.STRING.MAX(256))
    .required(VALIDATION_ERROR.STRING.REQUIREDx('Emai')),
})

export function ForgotPassword() {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [showResendEmailComfirmation, setShowResendEmailComfirmation] = useState(false);
  const [resendStatus, setResendStatus] = useState<{status: boolean, message: string} | undefined>(undefined)

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setShowResendEmailComfirmation(false)
      requestPasswordReset(values.email)
        .then(({data: {result}}) => {
          setHasErrors(false)
        })
        .catch((error) => {
          if (error.response.data.Message === "Account not verified.") {
            setShowResendEmailComfirmation(true)
            setHasErrors(false)
          }
          else {
            console.error(error)
            setHasErrors(true)
            setStatus(error.response.data.Message)
          }
        })
        .finally(() => {
          setLoading(false)
          setSubmitting(false)
        })
    },
  })

  const handleCancelBtnClick = () => {
    if (!formik.isSubmitting) navigate('/auth/login')
  }

  const handleResendEmailConfirmation = () => {
    setLoading(true)
    setResendStatus(undefined)
    resendEmailConfirmation(formik.values.email)
        .then(() => {
          setLoading(false)
          console.log("success")
          setResendStatus({status: true, message: "Verification Email has been sent, please check your mailbox for further instruction."})
        })
        .catch((error) => {
          console.log(error.response.data.Message);
          setResendStatus({status: false, message: error.response.data.Message})
        })
        .finally(() => {
          setLoading(false)
          setShowResendEmailComfirmation(true)
          setHasErrors(false)
        })
  }

  return (
    <div className='w-100 mw-450px h-100 d-flex flex-center'>
      <Row className='g-10'>
        {hasErrors === false && showResendEmailComfirmation === false && (
          <>
            <Col xs={12} className='text-center'>
              <Image className='h-100px mb-4' src={toAbsoluteUrl('/media/auth/ok_w.png')} />
              <div className='separator separator-content mt-4 mb-6'>
                <h2 className='text-gray-900 fs-1 text-nowrap'>Password Reset Email Sent</h2>
              </div>
            </Col>
            <Col xs={12} className='text-center'>
              <p>
                We've sent a password reset to your email at <b>{formik.values.email}</b>. Please check your email for further instructions.
              </p>
            </Col>
            <Col xs={12}>
              <Form>
                <Form.Group>
                  <div className='d-flex gap-2'>
                    <Button variant='light' className='flex-grow-1' onClick={handleCancelBtnClick}>
                      Go Back
                    </Button>
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </>
        )}
        {hasErrors === false && showResendEmailComfirmation === true && (
          <>
            <Col xs={12} className='text-center'>
              <Image className='h-100px mb-4' src={toAbsoluteUrl('/media/auth/please-verify-your-email_w.png')} />
              <div className='separator separator-content mt-4 mb-6'>
                <h2 className='text-gray-900 fs-1 text-nowrap'>Account Not Verified</h2>
              </div>
            </Col>
            <Col xs={12} className='text-center'>
              <p>You must activate your account first by verifying from your email address <b>{formik.values.email}</b>.</p>
              <p>
                If you've not received the verfication email, please use the <b>Resend Verification Email</b> button below and follow the instruction in the email to activate your account.
              </p>
            </Col>
            <Col xs={12}>
              <Form>
                <Form.Group>
                  <div className='d-flex gap-2'>
                    {(resendStatus === undefined || !resendStatus?.status) && (
                      <Button className='flex-grow-1' onClick={handleResendEmailConfirmation} disabled={loading}>
                        {!loading && <span className='indicator-label'>Resend Verfication Email</span>}
                        {loading && (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                            Please wait...
                          </span>
                        )}
                      </Button>
                    )}
                    {resendStatus?.status && (
                      <Button variant='light' className='flex-grow-1' onClick={handleCancelBtnClick} disabled={loading}>
                        Close
                      </Button>
                    )}
                  </div>
                  {resendStatus !== undefined && (
                    <div className='d-flex mt-5'>
                      <Alertize
                      variant={resendStatus.status ? 'success' : 'danger'}
                      faIcon={`fas fa-triangle-exclamation`}
                      description={<>{resendStatus.message}</>}
                      />
                    </div>
                  )}
                </Form.Group>
              </Form>
            </Col>
          </>
        )}
        {(hasErrors || hasErrors === undefined) && (
          <>
            <Col xs={12} className='text-center'>
              <div className='separator separator-content mt-4 mb-6'>
                <h2 className='text-gray-900 fs-1 text-nowrap'>Forgot Password?</h2>
              </div>
              <span>Enter your email to reset your password.</span>
            </Col>
            <Col xs={12}>
              <Form>
                {hasErrors === true && (
                  <Alertize
                    variant='danger'
                    faIcon={`fas fa-triangle-exclamation`}
                    description={formik.status}
                  />
                )}

                <Form.Group>
                  <Form.Label className={`fw-bold fs-6 required`}>Email</Form.Label>
                  <Col className='d-flex flex-column gap-2'>
                    <Form.Control
                      {...formik.getFieldProps('email')}
                      className={`${GetFormikValidClass(formik, 'email')}`}
                      type='email'
                      placeholder='Email'
                      value={formik.values.email || ''}
                      maxLength={256}
                      disabled={formik.isSubmitting}
                      autoComplete='off'
                    />

                    <FormikErrors formik={formik} fieldKey={'email'} />
                  </Col>
                </Form.Group>

                <Form.Group className='mt-10'>
                  <div className='d-flex gap-2'>
                    <Button
                      type='submit'
                      className='flex-grow-1'
                      onClick={(e) => {
                        e.preventDefault()
                        formik.handleSubmit()
                      }}
                      disabled={formik.isSubmitting}
                    >
                      {!loading && <span className='indicator-label'>Submit</span>}
                      {loading && (
                        <span className='indicator-progress' style={{display: 'block'}}>
                          <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                          Please wait...
                        </span>
                      )}
                    </Button>
                    {!loading && (
                      <Button variant='light' className='flex-grow-1' onClick={handleCancelBtnClick} disabled={formik.isSubmitting}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </>
        )}
      </Row>
    </div>
  )
}
